import React, { useCallback, useEffect, useState, useRef } from 'react';
import moment, { Moment } from 'moment';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import DatePicker from 'commons/src/components/DatePicker';
import { Store } from '../../reducers';
import styles from './DateRangeSelector.module.scss';

export type DateRangeSelectorProps = {
    /** Callback to set the start date */
    setStartDate: (date: Moment | null) => void;
    /** Callback to set the end date */
    setEndDate: (date: Moment | null) => void;
    /** Currently selected start date */
    startDate: Moment | null;
    /** Currently selected end date */
    endDate: Moment | null;
    /** Minimum number of days between start and end date */
    minNumberOfDays: number;
    /** Maximum number of days between start and end date (optional) */
    maxNumberOfDays?: number;
    /** Callback to notify parent when dates are valid/invalid */
    setDatesAreValid: (valid: boolean) => void;
    /** Whether to validate and display validation errors */
    validateSelection: boolean;
    /** Whether the date pickers are disabled */
    disabled?: boolean;
    /** Optional info text key for the start date label */
    startDateInfoText?: string;
    /** Optional info text key for the end date label */
    endDateInfoText?: string;
    /** When true, hide the "Start date" and "End date" labels (e.g. when only a parent "Time period" label is shown) */
    hideFieldLabels?: boolean;
    /** When true, do not show the inline red hint under the end date when the range exceeds max days (e.g. when the parent shows a block-level message instead) */
    suppressMaxDaysInlineHint?: boolean;
};

/**
 * DateRangeSelector Component
 *
 * A reusable date range picker component with validation support.
 * Provides two date pickers (start and end) with automatic validation
 * based on minimum and maximum day constraints.
 *
 * Features:
 * - Automatic end date picker opening after start date selection
 * - Date range validation (min/max days)
 * - Prevents selecting future dates
 * - Visual validation feedback
 *
 * @example
 * ```tsx
 * const [startDate, setStartDate] = useState<Moment | null>(null);
 * const [endDate, setEndDate] = useState<Moment | null>(null);
 * const [datesAreValid, setDatesAreValid] = useState(false);
 *
 * <DateRangeSelector
 *   setStartDate={setStartDate}
 *   setEndDate={setEndDate}
 *   startDate={startDate}
 *   endDate={endDate}
 *   minNumberOfDays={7}
 *   maxNumberOfDays={30}
 *   setDatesAreValid={setDatesAreValid}
 *   validateSelection={true}
 * />
 * ```
 */
const getEndDateLabel = (
    hideFieldLabels: boolean,
    minNumberOfDays: number,
    txt: (key: string, vars?: { [key: string]: string }) => string
): string => {
    if (hideFieldLabels) {
        return '';
    }
    if (minNumberOfDays !== 0) {
        const minDaysText = txt('MinNumberOfMeasurementDays', { days: minNumberOfDays.toString() });
        return `${txt('EndDate')} ${minDaysText}`;
    }
    return txt('EndDate');
};

const DateRangeSelector: React.FC<DateRangeSelectorProps> = ({
    setStartDate,
    setEndDate,
    startDate,
    endDate,
    minNumberOfDays,
    maxNumberOfDays,
    setDatesAreValid,
    validateSelection,
    disabled = false,
    startDateInfoText,
    endDateInfoText,
    hideFieldLabels = false,
    suppressMaxDaysInlineHint = false,
}) => {
    const dateFormat = useSelector((state: Store) => state.userSettings.dateFormat);
    const { t: txt } = useTranslation();
    const [displayValidationStartDate, setDisplayValidationStartDate] = useState(false);
    const [displayValidationEndDate, setDisplayValidationEndDate] = useState(false);
    const [endDateHint, setEndDateHint] = useState(
        txt('RadonReportEndDateHint', { minDays: minNumberOfDays.toString() })
    );
    const endDatePickerRef = useRef<HTMLDivElement>(null);
    const [shouldOpenEndDate, setShouldOpenEndDate] = useState(false);

    const onEndDateChange = (date: Moment): void => {
        const endOfSelectedDay = date && date.endOf('day');
        setEndDate(endOfSelectedDay);
        setDisplayValidationEndDate(false);
        setDisplayValidationStartDate(false);
        setShouldOpenEndDate(false);
    };

    const onStartDateChange = (date: Moment): void => {
        const startOfSelectedDay = date && date.startOf('day');
        setStartDate(startOfSelectedDay);
        setDisplayValidationStartDate(false);
        setDisplayValidationEndDate(false);

        // Auto-open end date picker after a short delay
        if (date) {
            setShouldOpenEndDate(true);
        }
    };

    useEffect(() => {
        if (shouldOpenEndDate && startDate) {
            // Use a small delay to ensure the DOM is updated
            const timer = setTimeout(() => {
                const endDateInput = endDatePickerRef.current?.querySelector('input') as HTMLInputElement;
                if (endDateInput) {
                    endDateInput.focus();
                    // Trigger click to open the picker
                    endDateInput.click();
                }
                setShouldOpenEndDate(false);
            }, 150);
            return () => clearTimeout(timer);
        }
        return undefined;
    }, [shouldOpenEndDate, startDate]);

    const outsideStartDateRange = (day: Moment): boolean => {
        if (!moment.isMoment(day)) return false;
        return day.isAfter(moment().subtract(1, 'days').endOf('day'));
    };

    const outsideEndDateRange = (day: Moment): boolean => {
        if (!moment.isMoment(day)) return false;
        if (!startDate) return true;

        const afterEndDate = moment(day).endOf('day').isAfter(moment().endOf('day'));
        const beforeStartDate = moment(day).endOf('day').isBefore(moment(startDate).startOf('day'));
        const lessThanMinNumberOfDays = moment(day)
            .endOf('day')
            .isBefore(moment(startDate).add(minNumberOfDays, 'days').endOf('day'));

        return afterEndDate || beforeStartDate || lessThanMinNumberOfDays;
    };

    const validateDates = useCallback((): void => {
        const segmentEnd = moment().endOf('day');
        const endDateOnOrBeforeToday = endDate && !endDate.isAfter(segmentEnd);
        const endDateValid =
            !!(endDate && !startDate) ||
            !!(
                endDate &&
                startDate &&
                endDate.isAfter(moment(startDate).add(minNumberOfDays - 1, 'days'), 'day') &&
                endDateOnOrBeforeToday
            ) ||
            false;

        const startDateValid =
            !!(!endDate && startDate) ||
            (endDate && startDate && startDate.isBefore(moment(endDate).endOf('day'), 'hour')) ||
            false;
        let withinMaxNumberOfDays = true;
        if (startDate && endDate && maxNumberOfDays) {
            withinMaxNumberOfDays = moment(endDate).diff(moment(startDate), 'days') < maxNumberOfDays;
        }
        setDatesAreValid(withinMaxNumberOfDays && endDateValid && startDateValid);
        if (validateSelection) {
            setDisplayValidationStartDate(!startDateValid);
            let updatedEndDateHint = txt('RadonReportEndDateHint', { minDays: minNumberOfDays.toString() });
            if (!endDateOnOrBeforeToday) updatedEndDateHint = txt('ReportEndDateBeforeEndHint');
            else if (maxNumberOfDays && !withinMaxNumberOfDays && !suppressMaxDaysInlineHint) {
                updatedEndDateHint = txt('Insight.MaxDaysHint', { maxDays: maxNumberOfDays.toString() });
            }
            setEndDateHint(updatedEndDateHint);
            const suppressValidationForMaxDays =
                suppressMaxDaysInlineHint &&
                !!maxNumberOfDays &&
                !withinMaxNumberOfDays &&
                endDateValid &&
                startDateValid;
            const showEndDateValidation = suppressValidationForMaxDays
                ? false
                : !endDateValid || !withinMaxNumberOfDays;
            setDisplayValidationEndDate(showEndDateValidation);
        }
    }, [validateSelection, startDate, endDate, suppressMaxDaysInlineHint, minNumberOfDays, maxNumberOfDays, txt]);

    useEffect(() => {
        validateDates();
    }, [validateDates]);

    return (
        <div className={styles.dateRangeContainer}>
            <div className={styles.datePickerWrapper}>
                <DatePicker
                    id="startDate"
                    defaultValue={startDate || undefined}
                    onChange={onStartDateChange}
                    dateFormat={dateFormat}
                    label={hideFieldLabels ? '' : 'StartDate'}
                    placeholder="StartDate"
                    mandatory
                    initialVisibleMonth={moment().subtract(1, 'months')}
                    disabled={disabled}
                    disabledDate={outsideStartDateRange}
                    displayDateValidation={displayValidationStartDate}
                    selectedDate={startDate}
                    selectedDateHint="ReportStartDateHint"
                    testId="start-date"
                    infoText={startDateInfoText}
                />
            </div>
            <div className={styles.datePickerWrapper} ref={endDatePickerRef}>
                <DatePicker
                    id="endDate"
                    defaultValue={endDate || undefined}
                    onChange={onEndDateChange}
                    dateFormat={dateFormat}
                    label={getEndDateLabel(hideFieldLabels, minNumberOfDays, txt)}
                    placeholder="EndDate"
                    mandatory
                    initialVisibleMonth={startDate || moment().subtract(1, 'months')}
                    disabled={disabled}
                    disabledDate={outsideEndDateRange}
                    displayDateValidation={displayValidationEndDate}
                    selectedDate={endDate}
                    selectedDateHint={endDateHint}
                    testId="end-date"
                    infoText={endDateInfoText}
                />
            </div>
        </div>
    );
};

export default DateRangeSelector;
